import {
  TopicPart,
  TopicPartFormData,
} from "../../core/topicPart/entities/TopicPart";
import { pb } from "../pocketbase/pocketbase";
import { RepositoryInterface } from "../interfaces/RepositoryInterface";
import { topicPartQuestionRepository } from "./topicPartQuestionRepository";

const collection = "topicParts";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRecordToTopicPart(record: any): TopicPart {
  return {
    id: record.id,
    topicId: record.topicId ?? record.topic_id,
    title: record.title,
    description: record.description ?? null,
    timing: record.timing,
    videoLink: record.videoLink ?? record.videoLink ?? null,
    videoFile: fileUrl(record, record.videoFile),
    audioFile: fileUrl(record, record.audioFile),
    contentText: record.contentText ?? record.contentText ?? "",
    additionalText: record.additionalText ?? record.additionalText ?? null,
    rank: record.rank,
    comments: record.comments,
    answerTogether: record.answerTogether ?? record.answerTogether ?? false,
    isSynthesisPart: record.isSynthesisPart ?? record.isSynthesisPart ?? false,
    topicPartQuestions: [],
    created: record.created ?? record.createdAt ?? null,
    updated: record.updated ?? record.updatedAt ?? null,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fileUrl(record: any, fileName: string | undefined): string | null {
  return fileName ? pb.files.getURL(record, fileName) : null;
}

/// Valeur à envoyer pour un champ fichier : le fichier sélectionné, `""` pour le supprimer, ou
/// `undefined` (champ omis) quand la valeur est l'URL du fichier déjà enregistré.
function filePayload(value: string | File | null | undefined): File | "" | undefined {
  if (value instanceof File) return value;
  if (value === null || value === "") return "";
  return undefined;
}

function topicPartFormToPayload(
  data: TopicPartFormData,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    topicId: data.topicId,
    title: data.title,
    description: data.description,
    timing: data.timing,
    videoLink: data.videoLink,
    contentText: data.contentText,
    additionalText: data.additionalText,
    answerTogether: data.answerTogether,
    isSynthesisPart: data.isSynthesisPart,
    comments: data.comments,
  };

  const videoFile = filePayload(data.videoFile);
  if (videoFile !== undefined) payload.videoFile = videoFile;
  const audioFile = filePayload(data.audioFile);
  if (audioFile !== undefined) payload.audioFile = audioFile;

  return payload;
}

export const topicPartRepository: RepositoryInterface<
  TopicPart,
  TopicPartFormData
> = {
  async getAll(topicId: string): Promise<TopicPart[]> {
    const records = await pb.collection(collection).getFullList({
      filter: `topicId = "${topicId}"`,
      sort: "rank",
    });

    return records.map(mapRecordToTopicPart);
  },

  async getOne(id: string): Promise<TopicPart> {
    const record = await pb.collection(collection).getOne(id);
    const part = mapRecordToTopicPart(record);
    part.topicPartQuestions = await topicPartQuestionRepository.getAll(id);
    return part;
  },

  async addUpdate(
    data: TopicPartFormData,
    id: string | null,
  ): Promise<TopicPart> {
    const payload = topicPartFormToPayload(data);
    let record;
    if (id) {
      record = await pb.collection(collection).update(id.toString(), payload);
    } else {
      record = await pb.collection(collection).create(payload);
    }

    return mapRecordToTopicPart(record);
  },

  async delete(id: string) {
    await pb.collection(collection).delete(id.toString());
  },

  async updateOrder(list: TopicPart[]) {
    const updates = list.map((topicPart, index) => ({
      id: topicPart.id,
      rank: index + 1,
    }));

    await pb.send("/api/reorder", {
      method: "POST",
      body: {
        collection,
        data: updates,
      },
    });
  },
};
